import * as Sentry from '@sentry/react';
import {atom, Atom, useAtom, useAtomValue} from 'jotai';
import React, {PropsWithChildren, useRef, useState} from 'react';
import {Navigate, useNavigate} from 'react-router';
import {GenericErrorMsg} from '~/entities/dialogs';
import {LoadingScreen} from '~/entities/fallbacks';
import {UserStatus} from '~/entities/user';
import {RootLayout, RsnDialog} from '~/library/ui-kit';
import {useKycStartMutation} from '~/pages/old-onboarding/api/useKycStartMutation';
import {
    ServiceUnavailableDialog,
    StepAlreadyCompletedDialog,
    useDialogs,
} from '~/pages/old-onboarding/ui/Signup/Dialogs';
import {ConfirmPassword} from '~/processes/onboarding/ui/kyc/ConfirmPassword';
import {appSessionStorage, atomWithStorageAccessor} from '~/shared/persistence';
import {useAppDispatch} from '~/shared/store';
import {useKycFinishMutation} from '../../api/useKycFinishMutation';
import Awaiting from './KYC/Awaiting';
import Finish from './KYC/Complete';
import {Intro} from './KYC/Intro';
import {KYCForm} from './KYCForm';

interface UserInfoProps {
    username: string;
    uuid: string;
    status: UserStatus;
}

const userStatusRaw$ = atomWithStorageAccessor(appSessionStorage.authStatus);
const userStatus$ = atom(get => get(userStatusRaw$) ?? undefined, userStatusRaw$.write);

function checkIsCIS(username: string) {
    const CISArray = ['+994', '+374', '+375', '+996', '+373', '+992', '+993', '+998', '+380', '+7'];
    return CISArray.some(code => username.includes(code));
}

export function KYC(props: UserInfoProps) {
    const dispatch = useAppDispatch();

    const [status = props.status, setStatus] = useAtom(userStatus$);
    const continueStep = useRef<UserStatus>(status ?? props.status);

    const [token, setToken] = useState('');

    const [dialog, setDialog] = useDialogs();
    const navigate = useNavigate();
    const navigateBack = () => navigate(-1);

    const isCIS = checkIsCIS(props.username);

    const handleError = (error: unknown) => {
        dispatch(GenericErrorMsg.open());
        throw error;
    };

    const {mutateAsync: mutateStart, isPending: isNewTokenPending} = useKycStartMutation();

    const fetchToken = async () => {
        const {value, problem} = await mutateStart().catch(handleError);
        if (problem) {
            switch (problem.code) {
                case 1:
                    setDialog('service-unavailable');
                    return;
                case 2:
                    setDialog('step-already-completed');
                    continueStep.current = problem.status;
                    return;
            }
        }
        setToken(value.token);
        setStatus(value.status);
    };

    const getNewToken = async () => {
        const {value, problem} = await mutateStart().catch(handleError);
        if (problem) {
            throw new Error(problem.message);
        }
        return value.token;
    };

    const {mutateAsync: mutateFinish} = useKycFinishMutation();
    const finishKyc = async () => {
        const {value} = await mutateFinish().catch(handleError);
        // Logs were added in https://jira.raison.digital/browse/RAISON-6159 for debugging purposes
        if (value.status === 'draft' || value.status === 'kyc_start') {
            Sentry.captureException(new Error('Received "draft" or "kyc_start" status after finishing KYC.'), {
                level: 'warning',
                extra: {
                    'Old status': status,
                    'New status': value.status,
                },
            });
        }
        setStatus(value.status);
    };

    const handleContinue = () => {
        navigateBack();
        setStatus(continueStep.current);
    };

    return (
        <>
            {(status === 'draft' || status === 'kyc_start') &&
                (!token ? (
                    <KycLayout title='Almost there…'>
                        <Intro onApply={fetchToken} isPending={isNewTokenPending} isCIS={isCIS} />
                    </KycLayout>
                ) : (
                    <KycLayout title='Verify your identity' navigateBack={false}>
                        <KYCForm token={token} getNewToken={getNewToken} onFinish={finishKyc} />
                    </KycLayout>
                ))}
            {status === 'moderating' && (
                <KycLayout title='Almost there…'>
                    <Awaiting />
                </KycLayout>
            )}
            {status === 'kyc_completed' && (
                <KycLayout title='Password confirmation'>
                    <ConfirmPassword />
                </KycLayout>
            )}
            {status === 'kyc_finish' && (
                <KycLayout title='Almost there…'>
                    <Finish />
                </KycLayout>
            )}
            {status === 'stuck' && <Navigate replace to='/signup/stuck' />}

            <RsnDialog.Controller onClose={navigateBack}>
                {(() => {
                    switch (dialog) {
                        case 'service-unavailable':
                            return <ServiceUnavailableDialog />;
                        case 'step-already-completed':
                            return <StepAlreadyCompletedDialog onContinue={handleContinue} />;
                    }
                })()}
            </RsnDialog.Controller>
        </>
    );
}

function KycLayout({title, navigateBack, children}: PropsWithChildren<{title: string; navigateBack?: false}>) {
    return (
        <RootLayout title={title} navigateBack={navigateBack}>
            <div className='flex flex-1 flex-col p-4'>{children}</div>
        </RootLayout>
    );
}

export default function KYCWithUserInfo({userInfo$, ...props}: {userInfo$: Atom<{data?: UserInfoProps}>}) {
    const {data} = useAtomValue(userInfo$);
    if (!data) {
        return <LoadingScreen />;
    }
    return <KYC {...props} {...data} />;
}
